import { useState } from 'react';
import { Loader2, MoreHorizontal, RotateCcw, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { type PendingInvite } from '@/hooks/admin/member/use-pending-invites';
import { useDeleteInvite } from '@/hooks/admin/member/use-delete-invite';
import { useInviteMembers } from '@/hooks/admin/member/use-invite-members';
import {
  StyledTable,
  StyledTableWrapper,
  StyledTableHeader,
  StyledTableBody,
  StyledTableRow,
  StyledTableHead,
  StyledTableCell,
} from '@/components/ui/styled-table';
import { getEmailDisplay } from '@/types/user';

interface InvitedMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invites: PendingInvite[];
  isLoading?: boolean;
  onInvitesChanged?: () => unknown | Promise<unknown>;
}

type InviteAction = 'cancel' | 'resend';
const expiredBadgeClassName =
  'bg-red-20 text-red-800 dark:bg-red-800/20 dark:text-red-400 border-red-400';
const setupBadgeClassName =
  'bg-yellow-20 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400 border-yellow-400';

function isInviteExpired(invite: PendingInvite): boolean {
  return new Date(invite.staleAt).getTime() <= Date.now();
}

export function InvitedMembersDialog({
  open,
  onOpenChange,
  invites,
  isLoading,
  onInvitesChanged,
}: InvitedMembersDialogProps) {
  const [pendingAction, setPendingAction] = useState<{
    email: string;
    action: InviteAction;
  } | null>(null);
  const [openDropdownEmail, setOpenDropdownEmail] = useState<string | null>(
    null
  );
  const { deleteInvite, isDeleting } = useDeleteInvite();
  const { inviteMembers, isInviting } = useInviteMembers();

  const sortedInvites = [...invites].sort((left, right) => {
    const leftExpired = isInviteExpired(left);
    const rightExpired = isInviteExpired(right);

    if (leftExpired !== rightExpired) {
      return leftExpired ? 1 : -1;
    }

    return (
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  });

  const handleInviteAction = async (email: string, action: InviteAction) => {
    try {
      setPendingAction({ email, action });
      setOpenDropdownEmail(null);

      if (action === 'cancel') {
        await deleteInvite({ email });
      } else {
        await inviteMembers({ emails: [email], apiMembers: [] });
      }

      await onInvitesChanged?.();
    } catch (error) {
      console.error(`Failed to ${action} invite:`, error);
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[80vh] overflow-auto"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Invited Members</DialogTitle>
          <DialogDescription>
            These members have been invited but haven&apos;t completed setup.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              Loading invites...
            </div>
          ) : sortedInvites.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              No pending invitations
            </div>
          ) : (
            <StyledTableWrapper>
              <StyledTable>
                <StyledTableHeader>
                  <tr>
                    <StyledTableHead>Email</StyledTableHead>
                    <StyledTableHead className="w-28" />
                    <StyledTableHead className="w-16" />
                  </tr>
                </StyledTableHeader>
                <StyledTableBody>
                  {sortedInvites.map((invite) => {
                    const expired = isInviteExpired(invite);
                    const rowIsBusy = pendingAction?.email === invite.email;
                    const rowKey = `${invite.email}-${invite.createdAt}`;

                    return (
                      <StyledTableRow key={rowKey}>
                        <StyledTableCell>
                          <span className="font-medium">
                            {getEmailDisplay(invite.email)}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell>
                          {/* Case: consumed invitee has not finished onboarding, so show setup state instead of invite expiration. */}
                          {invite.status === 'setup_incomplete' ? (
                            <Badge
                              variant="outline"
                              className={setupBadgeClassName}
                            >
                              Setup incomplete
                            </Badge>
                          ) : expired ? (
                            <Badge
                              variant="outline"
                              className={expiredBadgeClassName}
                            >
                              Expired
                            </Badge>
                          ) : null}
                        </StyledTableCell>
                        <StyledTableCell className="w-16 text-right">
                          <DropdownMenu
                            open={openDropdownEmail === invite.email}
                            onOpenChange={(nextOpen) =>
                              setOpenDropdownEmail(
                                nextOpen ? invite.email : null
                              )
                            }
                          >
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="ml-auto h-9 w-9"
                                disabled={rowIsBusy || isDeleting || isInviting}
                              >
                                {rowIsBusy ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreHorizontal className="h-4 w-4" />
                                )}
                                <span className="sr-only">
                                  Open invite actions
                                </span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              {/* Case: only unconsumed invites can be resent; setup-incomplete users must continue onboarding. */}
                              {invite.status === 'invited' ? (
                                <DropdownMenuItem
                                  disabled={
                                    rowIsBusy || isDeleting || isInviting
                                  }
                                  onClick={() =>
                                    handleInviteAction(invite.email, 'resend')
                                  }
                                >
                                  <RotateCcw className="h-4 w-4" />
                                  <span>Resend</span>
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem
                                disabled={rowIsBusy || isDeleting || isInviting}
                                onClick={() =>
                                  handleInviteAction(invite.email, 'cancel')
                                }
                              >
                                <X className="h-4 w-4" />
                                <span>Cancel</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </StyledTableBody>
              </StyledTable>
            </StyledTableWrapper>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
